import { useState } from "react";
import { Info, Shield, Heart, Home, Car, Briefcase, Plane, Bike } from "lucide-react";

// Mock detailed content for all insurance types
const insuranceData = {
  health: {
    title: "Health Insurance",
    icon: <Heart className="w-6 h-6" />,
    description:
      "Health Insurance is a financial agreement that ensures the policyholder gets access to quality medical care without the burden of out-of-pocket expenses. In a world where medical inflation is rising at double-digit rates, health insurance acts as your financial firewall—covering expenses ranging from hospitalization and surgery to diagnostics and post-operative care.\n\nWhether it's a sudden illness, accident, chronic condition, or planned procedure, health insurance ensures that you or your family members receive timely and uninterrupted care without the stress of high costs.",
    whyNecessary: [
      {
        title: "Covers Hospitalization Costs",
        description:
          "Health insurance policies typically cover room rent, ICU charges, nursing expenses, and treatment during hospital admission, which can be financially draining without insurance.",
      },
      {
        title: "Pre and Post-Hospitalization Expenses",
        description:
          "Most plans cover medical costs incurred 30 to 60 days before and after hospitalization, such as consultations, diagnostics, and follow-up treatments.",
      },
      {
        title: "Cashless Facility at Network Hospitals",
        description:
          "With access to thousands of empaneled hospitals, policyholders can avail of treatment without paying upfront—the insurer settles the bill directly with the hospital.",
      },
      {
        title: "Coverage for Surgeries and Critical Procedures",
        description:
          "Be it an organ transplant, bypass surgery, joint replacement, or cancer treatment, health insurance supports expensive procedures that could otherwise drain life savings.",
      },
      {
        title: "Maternity and Newborn Benefits",
        description:
          "Many modern plans offer maternity cover including delivery, C-section, prenatal and postnatal expenses, and even newborn care for the first few months.",
      },
      {
        title: "Day Care Procedures",
        description:
          "Not all treatments require 24-hour hospitalization. Health insurance now covers day care procedures such as cataract surgeries, chemotherapy, and dialysis.",
      },
      {
        title: "Mental Health and OPD Coverage",
        description:
          "Some comprehensive plans include outpatient consultations and mental health treatment, addressing newer health challenges holistically.",
      },
      {
        title: "Alternative Treatments (AYUSH)",
        description:
          "Several policies also cover alternative treatments under Ayurveda, Yoga, Unani, Siddha, and Homeopathy, giving users more flexibility in care.",
      },
      {
        title: "No-Claim Bonus (NCB)",
        description:
          "For every year you don't raise a claim, your sum insured increases without an increase in premium—often up to 50%-100%.",
      },
      {
        title: "Tax Benefits",
        description:
          "Under Section 80D of the Income Tax Act, premiums paid toward health insurance qualify for tax deductions, giving you a dual financial advantage.",
      },
    ],
    types: [
      {
        title: "Individual Health Insurance",
        description:
          "Covers a single person and reimburses for hospital and treatment-related expenses.",
      },
      {
        title: "Family Floater Plans",
        description:
          "A single sum insured covers the entire family, making it cost-effective and convenient.",
      },
      {
        title: "Senior Citizen Health Insurance",
        description:
          "Tailored for individuals aged 60 and above, these plans offer higher coverage, pre-existing condition coverage, and annual check-ups.",
      },
      {
        title: "Critical Illness Plans",
        description:
          "Offer lump sum payment upon diagnosis of serious illnesses like cancer, stroke, or kidney failure.",
      },
      {
        title: "Top-Up and Super Top-Up Plans",
        description:
          "Designed to supplement your base policy, these plans offer higher coverage at lower premiums once a deductible is met.",
      },
      {
        title: "Group Health Insurance",
        description:
          "Offered by employers to employees, these plans ensure broad coverage with maternity, wellness, and sometimes dental benefits.",
      },
    ],
    additionalInfo: [
      "Pre-existing diseases have waiting periods, and early purchase ensures you're covered when needed.",
      "Premiums are lower at a younger age, making it more affordable and easier to upgrade later.",
      "Lifestyle illnesses like diabetes, hypertension, and obesity are rising even in younger demographics—making early planning vital.",
      "In case of future pandemics or lifestyle-related complications, a health plan acts as your first line of defense.",
    ],
    additionalTitle: "Why You Should Buy Health Insurance Early",
  },

  termLife: {
    title: "Term Life Insurance",
    icon: <Shield className="w-6 h-6" />,
    description:
    "Term Life Insurance is the simplest, most affordable, and purest form of life insurance. It offers a financial safety net to your family or dependents by providing a lump-sum payout (sum assured) in case of the policyholder’s untimely demise during the policy term. Unlike other life insurance plans, term plans do not offer maturity benefits but excel in high coverage at low premiums, ensuring that your loved ones are protected even when you are not around.\n\nIn today’s uncertain world, where rising expenses and debts are a part of everyday life, term insurance is one of the most responsible financial decisions you can make—especially if you’re the primary earning member of your family.",
    whyNecessary: [
      {
        title: "Affordable Premiums with High Coverage",
        description:
          "For the price of a coffee a day, you can secure a sum assured running into crores. A ₹1 crore cover can cost less than ₹1,000/month for a young individual, offering immense financial leverage.",
      },
      {
        title: "Family’s Financial Protection",
        description:
          " In your absence, term insurance provides your family with financial security—helping them meet day-to-day expenses, maintain lifestyle continuity, pay off loans, and secure children's future goals like education or marriage.",
      },
     
      {
        title: "Debt Repayment Support",
        description:
          "If you have ongoing loans (home, car, education, personal), the term insurance payout ensures that your family doesn't get burdened with EMIs or the risk of asset seizures.",
      },
      {
        title: "Flexible Payout Options",
        description:
          "Beneficiaries can choose to receive the death benefit as a lump sum, monthly income, or a combination of both—ensuring better money management over time.",
      },
      {
        title: "Rider Add-Ons for Enhanced Coverage",
        description:
          " Critical illness, accidental death benefit, waiver of premium, and permanent disability riders can be added for comprehensive protection against broader life risks.",
      },
      {
        title: "Income Replacement Strategy",
        description:
          "Term insurance acts as an income replacement plan. If your income stops due to death or disability, the insurance fills the gap and helps your family sustain themselves for years.",
      },
      {
        title: "Tax Benefits",
        description:
          "  Premiums paid qualify for tax deductions under Section 80C, and the death benefit is tax-free under Section 10(10D)—making it a powerful tax-saving tool.",
      },
      {
        title: "Peace of Mind in Risky Professions",
        description:
          "If you work in high-risk environments like construction, mining, aviation, or the military, term insurance becomes even more critical for your family’s future.",
      },

      {
        title: "Support for Young Families",
        description:
          " New parents and couples with young dependents can use term plans as a backbone for their future—securing their child’s dreams, education, and stability.",
      },
    ],
    types: [
      {
        title: "Level Term Plan",
        description:
          "Offers a fixed sum assured throughout the policy term. Ideal for general coverage needs.",
      },
      {
        title: "Increasing Term Plan",
        description:
          " The sum assured increases every year to counter inflation and rising financial needs.",
      },
      {
        title: "Decreasing Term Plan",
        description:
          "Commonly used to cover loans. The sum assured decreases with the outstanding loan balance over time.",
      },
      {
        title: "Return of Premium (TROP)",
        description:
          "Offers a refund of total premiums paid if the insured survives the policy term. While the premiums are higher, the plan ensures no monetary loss.",
      },
      {
        title: "Joint Life Term Plan",
        description:
          "Covers both spouses under one plan, with a payout on the first demise and continued coverage for the surviving partner depending on the plan.",
      },
      {
        title: "Convertible Term Plan",
        description:
          "Gives flexibility to convert your term policy into an endowment or whole-life plan at a later stage.",
      },
    ],
    additionalInfo: [
      "Early in Life (20s–30s): Premiums are the lowest when you’re young and healthy. Buying early locks in your health status and rate for the next 30–40 years.",
      "Before Marriage or Kids: A must-have if you’re starting a family, ensuring their protection from day one.",
      "With Loans and Financial Liabilities: If you’ve taken a home loan or have significant financial commitments, term insurance acts as a safety cushion.",
      "Business Owners: Entrepreneurs and business owners can use term insurance to ensure smooth succession planning and protect the financial future of partners or investors.",
    ],
    additionalTitle: "Key Considerations When Buying Term Life Insurance",
  },

//   home: {
//     title: "Home Insurance",
//     icon: <Home className="w-6 h-6" />,
//     description:
//       "Home Insurance protects your most valuable asset against damage from natural disasters, fire, theft, and other hazards. It typically covers both the physical structure of your home and your personal belongings inside it. Additionally, it provides liability protection if someone is injured on your property.",
//     whyNecessary: [
//       {
//         title: "Structure Protection",
//         description:
//           "Covers repair or rebuilding costs if your home is damaged by fire, storms, lightning, or other covered perils.",
//       },
//       {
//         title: "Personal Property Coverage",
//         description:
//           "Protects your belongings like furniture, electronics, clothing, and other items if they're damaged, destroyed, or stolen.",
//       },
//       {
//         title: "Liability Protection",
//         description:
//           "Offers coverage if someone is injured on your property and you're legally responsible for their medical bills or legal expenses.",
//       },
//       {
//         title: "Additional Living Expenses",
//         description:
//           "Pays for hotel stays, meals, and other costs if your home becomes uninhabitable due to a covered event.",
//       },
//       {
//         title: "Mortgage Requirement",
//         description:
//           "Most mortgage lenders require home insurance to protect their investment in your property.",
//       },
//       {
//         title: "Peace of Mind",
//         description:
//           "Provides financial security knowing that you're protected against potentially catastrophic expenses.",
//       },
//     ],
//     types: [
//       {
//         title: "HO-1 (Basic Form)",
//         description:
//           "Covers damage from basic perils like fire, lightning, windstorm, and theft.",
//       },
//       {
//         title: "HO-2 (Broad Form)",
//         description:
//           "Includes coverage for HO-1 perils plus falling objects, weight of ice/snow, and more.",
//       },
//       {
//         title: "HO-3 (Special Form)",
//         description:
//           "The most common policy; covers all perils except those specifically excluded.",
//       },
//       {
//         title: "HO-4 (Renters Insurance)",
//         description:
//           "Protects personal property for those who rent rather than own their home.",
//       },
//       {
//         title: "HO-5 (Comprehensive Form)",
//         description:
//           "Premium coverage for both the structure and contents against all perils except those specifically excluded.",
//       },
//       {
//         title: "HO-6 (Condo Insurance)",
//         description:
//           "Designed specifically for condominium owners, covering personal property and structural elements within the unit.",
//       },
//     ],
//     additionalInfo: [
//       "Regular home insurance typically doesn't cover flood or earthquake damage; separate policies are needed.",
//       "Conduct a home inventory with photos and receipts to make claims easier.",
//       "Consider actual cash value vs. replacement cost coverage for your personal belongings.",
//       "Homes in high-risk areas may need additional coverage for specific perils.",
//       "Many insurers offer discounts for security systems, smoke detectors, and other safety features.",
//     ],
//     additionalTitle: "Important Facts About Home Insurance",
//   },

  auto: {
    title: "Car Insurance",
    icon: <Car className="w-6 h-6" />,
    description:
      "Car insurance is a specialized form of motor insurance designed to safeguard your four-wheeler against potential risks such as accidents, theft, natural disasters, and third-party liabilities. As a car owner, it’s not just a legal mandate but a crucial financial shield that protects both your vehicle and your peace of mind.",
    whyNecessary: [
      {
        title: "Legal Mandate in India",
        description:
          "According to the Motor Vehicles Act, 1988, it is mandatory for every car owner to have at least a third-party insurance policy. Driving an uninsured car can lead to penalties, fines, and even legal trouble.",
      },
      {
        title: "Covers Own Damage (OD)",
        description:
          "In a comprehensive policy, damages to your own vehicle due to collisions, overturning, fire, or natural disasters like floods and earthquakes are covered extensively.",
      },
      {
        title: "Protection Against Theft",
        description:
          " If your vehicle gets stolen, your insurer compensates you as per the Insured Declared Value (IDV), reducing your financial burden drastically.",
      },
      {
        title: "Natural and Man-Made Calamities",
        description:
          " From cyclones and landslides to riots and vandalism, car insurance ensures you don’t bear the brunt of unexpected events alone.",
      },
      {
        title: "Third-Party Liability Coverage",
        description:
          "This covers legal liabilities arising out of injuries, death, or property damage to a third party due to your car.",
      },
      {
        title: "Personal Accident Cover",
        description:
          "Almost all car policies offer a personal accident cover for the owner-driver, covering death or disability arising from accidents.",
      },
      {
        title: "Add-on Covers for Enhanced Protection",
        description:
          "Options like Zero Depreciation, Roadside Assistance, Engine Protection, Consumables Cover, and Return to Invoice can tailor your policy to your specific needs.",
      },
      {
        title: "No Claim Bonus (NCB)",
        description:
          "You can enjoy discounts on renewal premiums for every claim-free year, encouraging safe and responsible driving.",
      },
    ],
    types: [
      {
        title: "Third-Party Car Insurance",
        description:
          "This is the minimum mandatory insurance required under Indian law. It covers damages or injuries caused to a third party (individual or property) due to your car.",
      },
      {
        title: " Comprehensive Car Insurance",
        description:
          "This is an all-in-one policy that covers third-party liabilities and damages to your own vehicle due to accidents, fire, theft, natural disasters, riots, and more.",
      },
      {
        title: "Standalone Own Damage (OD) Cover",
        description:
          "Available only for cars already insured under third-party policies, this plan covers damages to your own car without third-party coverage.",
      },
      {
        title: "Pay-As-You-Drive Insurance (Usage-Based)",
        description:
          "Premium is based on the number of kilometers driven. Low-mileage users can benefit from lower premiums.",
      },
      {
        title: "Zero Depreciation Car Insurance",
        description:
          "Covers the full cost of car parts without factoring in depreciation during claims.",
      },
      {
        title: "Return to Invoice (RTI) Cover",
        description:
          "In case of total loss or theft, you get the original invoice value of the car instead of the current market value.",
      },
      {
        title: "Engine Protection Cover",
        description:
          "Covers damages to the engine due to water ingress or oil leakage—typically not covered in standard plans.",
      },
    ],
    additionalInfo: [
      " At the Time of Purchase: Whether you’re buying a new or used car, insurance should be activated before hitting the road. Most dealers offer bundled policies, but comparing options independently may save you money and offer better coverage.",
      "   Before Registration: For a new vehicle, car insurance is mandatory to complete the registration process. A valid policy is required to legally drive your vehicle home from the showroom.",
      " After a Major Upgrade or Modification: If you've upgraded your car's engine, added expensive accessories, or enhanced performance, it's wise to review and upgrade your insurance policy accordingly. Modified vehicles may require a different cover.",
      " When Your Policy Is About to Expire: Renew your policy before the due date to avoid inspection hassles, loss of No Claim Bonus (NCB), or policy lapses. Even a single day's gap can be financially risky.",
      "   Before a Long Road Trip: Planning a highway trip or intercity drive? Make sure your policy includes breakdown assistance, towing, and extended coverage for added security on the move.",
      "  After Relocating to a New City or State: Your premium may change based on your new city’s risk profile. Intimate your insurer and review your policy to ensure continuous and relevant coverage.",
      "  When Changing Your Car Usage Patterns: Switching from occasional weekend drives to daily commutes increases risk exposure. Update your insurer and revise your policy if necessary.",
    ],
    additionalTitle: "When Should You Buy Car Insurance",
  },

  bike: {
    title: "Bike Insurance",
    icon: <Bike className="w-6 h-6" />,
    description:
      "Bike insurance, or two-wheeler insurance, is tailored to meet the protection needs of scooters, motorcycles, and electric bikes. With India being one of the largest markets for two-wheelers, bike insurance is not only mandatory by law but also essential due to higher vulnerability to accidents and theft.",
    whyNecessary: [
      {
        title: "Mandatory Under Law",
        description:
          " Just like cars, every two-wheeler in India must be insured with at least a third-party liability policy. Non-compliance can lead to heavy fines, seizure of the vehicle, or legal complications.",
      },
      {
        title: "High Vulnerability to Accidents",
        description:
          "Two-wheelers are more exposed to road risks than four-wheelers, making insurance vital to cover injuries and damages.",
      },
      {
        title: "Covers Own Damage (in Comprehensive Plans)",
        description:
          "Whether your bike skids, crashes, or is damaged due to floods or fire, a comprehensive policy ensures repairs and replacements don’t drain your savings.",
      },
      {
        title: "Theft and Total Loss Coverage",
        description:
          " In urban and semi-urban areas, two-wheeler theft is common. Insurance offers financial reimbursement in case the bike is stolen or irreparably damaged.",
      },
      {
        title: "Third-Party Protection",
        description:
          " Accidents that cause injury or property damage to others can invite legal and financial repercussions. Bike insurance ensures you’re covered in such scenarios.",
      },
      {
        title: "Personal Accident Cover for Ride",
        description:
          " Most policies include a PA cover for the owner-driver that offers compensation in the event of death or disability due to an accident.",
      },
      {
        title: "Affordable Premiums",
        description:
          "  Bike insurance, especially for gearless and lower engine capacity bikes, is highly economical. Multi-year policies offer additional savings and convenience.",
      },
      {
        title: "Useful Add-ons",
        description:
          " Add-ons like Engine Protect, Helmet Cover, and Roadside Assistance can be opted for extended benefits.",
      },
      {
        title: "No Claim Bonus (NCB)",
        description:
          "  For every year you don’t make a claim, you get a discount on renewal—up to 50% for five consecutive claim-free years.",
      },

    ],
    types: [
      {
        title: "Third-Party Bike Insurance",
        description:
          "Mandatory by law, this plan covers injuries or damages caused to a third party. It does not protect the bike or rider.",
      },
      {
        title: "Comprehensive Bike Insurance",
        description:
          "Covers third-party liabilities and own damage, including theft, fire, accidents, floods, and more.",
      },
      {
        title: "Standalone Own Damage Cover",
        description:
          "Designed for those who already have third-party coverage but need protection for their own two-wheeler.",
      },
      {
        title: "Long-Term Two-Wheeler Insurance",
        description:
          "Covers your bike for 2 to 5 years in one go, protecting against premium hikes and policy lapses.",
      },
      {
        title: "Zero Depreciation Cover",
        description:
          "Provides full compensation without accounting for depreciation, ensuring maximum claims on parts replacement.",
      },
      {
        title: "Personal Accident Cover",
        description:
          "Offers financial coverage in case of death or permanent disability due to a bike accident.",
      },
      {
        title: " NCB (No Claim Bonus) Protect",
        description:
          "Keeps your No Claim Bonus intact even after filing a claim during the policy period.",
      },
      {
        title: "Engine and Gearbox Protection Add-On",
        description:
          "Covers the cost of repairing internal damages to the engine and gearbox due to oil leaks or flooding.",
      },
    ],
    additionalInfo: [
      " Right After Buying the Bike: Don’t delay! Whether it’s a showroom purchase or a second-hand deal, insurance should be in place before the first ride. It’s your legal and financial armor.",
      "  Before First Registration: Bike insurance is mandatory for registration and on-road use. Many RTOs won’t issue a registration certificate without an active insurance policy.",
      " During Policy Renewal Window: Renew your policy a few days before it expires to avoid inspection delays and ensure continued coverage. Timely renewal also retains your NCB benefits and discounts.",
      " After Adding Accessories or Custom Parts: Installed a new exhaust, added alloy wheels, or wrapped your bike in matte? Inform your insurer and upgrade coverage to protect your investment.",
      "  Before Monsoon or Harsh Weather Seasons: Monsoon-related water damage or slippery roads can increase accident risk. Ensure your insurance includes natural calamity coverage before the season hits.",
      " Before Long-Distance Rides: If you’re planning a road trip or inter-state ride, make sure your policy includes roadside assistance, personal accident cover, and adequate third-party protection.",
      " When Moving to High-Risk Zones: Cities or areas with high traffic or theft rates may require a more comprehensive plan. Re-evaluate your policy when shifting locations.",
      " When Converting to Commercial Use: If you're planning to use your bike for deliveries or business, you’ll need a commercial insurance plan. Using a personal plan in such cases could void claims.",

    ],
    additionalTitle: "When Should You Buy Bike Insurance",
  },

  travel: {
    title: "Travel Insurance",
    icon: <Plane className="w-6 h-6" />,
    description:
    "Travel Insurance is a comprehensive policy designed to cover a wide array of unforeseen situations that can occur during domestic or international travel. From flight cancellations and medical emergencies to lost baggage and passport theft, travel insurance acts as your safety net, ensuring peace of mind while you explore new destinations.\n\nIn an age where travel has become more accessible but also more uncertain due to geopolitical tensions, health crises, or logistical disruptions, having a travel insurance plan is not just a smart choice—it’s often a travel essential.",
    whyNecessary: [
      {
        title: "Medical Emergencies Abroad",
        description:
          "Healthcare expenses in foreign countries—especially in places like the US, Europe, or Australia—can be exorbitant. Travel insurance covers hospitalization costs, outpatient treatments, and emergency surgeries, which can otherwise cost thousands of dollars.",
      },
      {
        title: "Trip Cancellation or Curtailment",
        description:
          "Sometimes trips get cancelled due to personal emergencies, sudden illness, natural disasters, or political unrest. A travel policy compensates you for non-refundable bookings such as flights, hotels, and activity reservations.",
      },
      {
        title: "Lost or Delayed Baggage",
        description:
          "If your checked-in luggage is delayed or permanently lost, travel insurance reimburses you for essentials like clothes, toiletries, and accessories, ensuring you don’t have to scramble in a new country.",
      },
      {
        title: "Personal Liability Coverage",
        description:
          "If you accidentally injure someone or cause damage to property while traveling, your travel policy can cover legal expenses and compensation—especially important in countries with strict laws.",
      },
      {
        title: "Flight Delays or Missed Connections",
        description:
          " Missed layovers due to delays can throw off your entire travel plan. Insurance helps cover rebooking costs, food, and accommodation during the wait.",
      },
      {
        title: "Emergency Evacuation & Repatriation",
        description:
          "Whether due to a natural calamity, medical necessity, or political instability, some situations require emergency evacuation or returning to your home country. Travel insurance covers these high-cost operations, including air ambulances.",
      },
      {
        title: "COVID-19 and Pandemic Coverage",
        description:
          "Many modern travel insurance plans offer benefits like quarantine expenses, RT-PCR test costs, and even coverage for cancelled bookings due to sudden lockdowns or positive test results.",
      },
      {
        title: "Hijack Distress Allowance",
        description:
          " If your flight is hijacked, some policies provide a daily allowance to help you through the mental and physical distress during such harrowing times.",
      },
      {
        title: "Home Burglary While Traveling",
        description:
          "Certain policies even offer protection for your residence while you're away, covering burglary or break-ins during the travel period.",
      },
    ],
    types: [
      {
        title: "Single Trip Insurance",
        description:
          "Ideal for one-time travelers, this covers your trip duration and ends once you return home.",
      },
      {
        title: "Multi-Trip Annual Plans",
        description:
          "Perfect for frequent flyers and business professionals, this policy covers unlimited trips within a year, offering convenience and cost-efficiency.",
      },
      {
        title: "Student Travel Insurance",
        description:
          "Specifically designed for students studying abroad, covering tuition fees in emergencies, medical expenses, sponsor protection, and even compassionate visits from family.",
      },
      {
        title: "Senior Citizen Travel Insurance",
        description:
          "Tailored for individuals aged 60 and above, this plan offers extended medical coverage, pre-existing disease protection, and assistance services suited to elder travelers.",
      },
      {
        title: "Group Travel Insurance",
        description:
          "Covers a group of individuals traveling together, often used for corporate off-sites, student tours, or family holidays.",
      },
      {
        title: "Schengen Travel Insurance",
        description:
          "Mandatory for Schengen visa holders, this policy is a requirement for entry into European countries and must meet specific EU guidelines.",
      },
    ],
    additionalInfo: [
      "Uncertainties are inevitable in travel—volcanic eruptions, border closures, pandemics, airline bankruptcies, and medical emergencies can turn a dream vacation into a financial nightmare.",
      "Many international embassies require travel insurance as part of the visa application process.",
      "Travel insurance offers not just compensation but access to global 24x7 assistance services, providing multilingual support, hospital recommendations, and emergency contacts.",
      "For adventure enthusiasts and thrill-seekers engaging in skiing, scuba diving, or trekking, travel insurance can cover accidents and evacuation from remote locations.",
      "Business travelers can benefit from cover for loss of laptops, documents, and business-related delays.",
    ],
    additionalTitle: "Why Travel Insurance Should Never Be Skipped",
  },
};

export default function InsuranceHomeComponent() {
  const [selectedType, setSelectedType] = useState("health");
  const [activeTab, setActiveTab] = useState("overview");

  // Function to get the correct data object based on the selected type
  const getTypeData = () => {
    switch (selectedType) {
      case "health":
        return insuranceData.health;
      case "term-life":
        return insuranceData.termLife;
    //   case "home":
    //     return insuranceData.home;
      case "auto":
        return insuranceData.auto;
      case "bike":
        return insuranceData.bike;
      case "travel":
        return insuranceData.travel;
      default:
        return insuranceData.health;
    }
  };

  const typeData = getTypeData();

  const insuranceTypes = [
    {
      id: "health",
      name: "Health Insurance",
      icon: <Heart className="w-6 h-6" />,
      highlight: true,
    },
    {
      id: "term-life",
      name: "Term Life Insurance",
      icon: <Shield className="w-6 h-6" />,
      highlight: true,
    },
    // { id: "home", name: "Home Insurance", icon: <Home className="w-6 h-6" /> },
    { id: "auto", name: "Car Insurance", icon: <Car className="w-6 h-6" /> },
    {
      id: "bike",
      name: "Bike Insurance",
      icon: <Bike className="w-6 h-6" />,
    },
    {
      id: "travel",
      name: "Travel Insurance",
      icon: <Plane className="w-6 h-6" />,
    },
  ];

  return (
    <div className="max-w-7xl mx-auto p-4">
       <h1 className="text-2xl md:text-3xl font-bold text-center text-gray-900 mb-6">
        📌 Important Types of Insurance
      </h1>
      <p className="text-center text-gray-600 mb-10">
      Insurance comes in a variety of forms, catering to different needs, stages of life, and asset types. While the insurance industry spans across dozens of niches, there are a few that are essential for every individual and family, either due to legal mandates, high risk, or financial prudence.
      </p>

      {/* Insurance Type Selection */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3 mb-8">
        {insuranceTypes.map((type) => (
          <button
            key={type.id}
            onClick={() => {
              setSelectedType(type.id);
              setActiveTab("overview");
            }}
            className={`p-4 rounded-lg flex flex-col items-center justify-center transition-all ${
              selectedType === type.id
                ? "bg-blue-500 text-white shadow-lg scale-105"
                : "bg-white text-gray-700 hover:bg-blue-100 shadow"
            }`}
          >
            <div
              className={`p-2 rounded-full mb-2 ${
                selectedType === type.id ? "bg-blue-500" : "bg-blue-100"
              }`}
            >
              {type.icon}
            </div>
            <span className="text-sm font-medium text-center">
              {type.highlight ? (
                <span className="relative">
                  {type.name}
                  {/* <span className="absolute -top-1 -right-3 text-blue-600 text-lg">
                    **
                  </span> */}
                </span>
              ) : (
                type.name
              )}
            </span>
          </button>
        ))}
      </div>

      {/* Content Display */}
      <div className="bg-white rounded-xl shadow-lg overflow-hidden">
        {/* Header */}
        <div className="flex items-center p-6 border-b border-gray-200">
          <div className={`p-3 rounded-full bg-blue-100 mr-4`}>
            {typeData.icon}
          </div>
          <h2 className="text-2xl font-bold text-gray-800">
            {insuranceTypes.find((t) => t.id === selectedType).highlight ? (
              <span className="relative">
                {typeData.title}
                {/* <span className="absolute -top-1 -right-3 text-blue-600 text-lg">
                  **
                </span> */}
              </span>
            ) : (
              typeData.title
            )}
          </h2>
        </div>

        {/* Tab Navigation */}
        <div className="flex border-b border-gray-200 overflow-x-auto">
          <button
            onClick={() => setActiveTab("overview")}
            className={`py-3 px-6 font-medium text-sm ${
              activeTab === "overview"
                ? "border-b-2 border-blue-600 text-blue-600"
                : "text-gray-500 hover:text-gray-700"
            }`}
          >
            Overview
          </button>
          <button
            onClick={() => setActiveTab("why")}
            className={`py-3 px-6 font-medium text-sm ${
              activeTab === "why"
                ? "border-b-2 border-blue-600 text-blue-600"
                : "text-gray-500 hover:text-gray-700"
            }`}
          >
            Why It's Necessary
          </button>
          <button
            onClick={() => setActiveTab("types")}
            className={`py-3 px-6 font-medium text-sm ${
              activeTab === "types"
                ? "border-b-2 border-blue-600 text-blue-600"
                : "text-gray-500 hover:text-gray-700"
            }`}
          >
            Types
          </button>
          <button
            onClick={() => setActiveTab("additional")}
            className={`py-3 px-6 font-medium text-sm ${
              activeTab === "additional"
                ? "border-b-2 border-blue-600 text-blue-600"
                : "text-gray-500 hover:text-gray-700"
            }`}
          >
            Additional Info
          </button>
        </div>

        {/* Tab Content */}
        <div className="p-6">
          {activeTab === "overview" && (
            <div className="prose max-w-none">
              <p className="text-gray-600 whitespace-pre-line">
                {typeData.description}
              </p>
              {/* <div className="mt-6 flex justify-end">
                <button
                  onClick={() => setActiveTab("why")}
                  className="bg-blue-600 text-white py-2 px-6 rounded-lg hover:bg-blue-700 transition-colors flex items-center"
                >
                  Learn More
                  <svg
                    className="w-4 h-4 ml-2"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M9 5l7 7-7 7"
                    ></path>
                  </svg>
                </button>
              </div> */}
            </div>
          )}

          {activeTab === "why" && (
            <div>
              <h3 className="font-bold text-lg text-gray-800 mb-4">
                Why {typeData.title} is Essential:
              </h3>
              <div className="space-y-4">
                {typeData.whyNecessary.map((item, index) => (
                  <div key={index} className="bg-blue-50 p-4 rounded-lg">
                    <h4 className="font-semibold text-blue-800">
                      {item.title}
                    </h4>
                    <p className="text-gray-700">{item.description}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {activeTab === "types" && (
            <div>
              <h3 className="font-bold text-lg text-gray-800 mb-4">
                Types of {typeData.title}:
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {typeData.types.map((item, index) => (
                  <div
                    key={index}
                    className="border border-gray-200 p-4 rounded-lg hover:shadow-md transition-shadow"
                  >
                    <h4 className="font-semibold text-gray-800">
                      {item.title}
                    </h4>
                    <p className="text-gray-600">{item.description}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {activeTab === "additional" && (
            <div>
              <h3 className="font-bold text-lg text-gray-800 mb-4">
                {typeData.additionalTitle}:
              </h3>
              <ul className="list-disc pl-5 space-y-2">
                {typeData.additionalInfo.map((item, index) => (
                  <li key={index} className="text-gray-700">
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>

      {/* Additional Information Panel */}
      {/* <div className="mt-6 bg-gray-100 rounded-lg p-4 text-sm text-gray-700">
        <p>
          <strong>Note:</strong> Insurance needs vary by individual
          circumstances. Consider consulting with a qualified insurance advisor
          to determine the right coverage for your specific situation.
        </p>
      </div> */}
    </div>
  );
}
